import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthContext, useAuthProvider } from './hooks/useAuth';
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';

// Public Pages
import Home from './pages/Home';
import ReturnToIndia from './pages/ReturnToIndia';
import RealEstate from './pages/RealEstate';
import ProjectDetail from './pages/ProjectDetail';
import About from './pages/About';
import BookConsultation from './pages/BookConsultation';
import BookingSuccess from './pages/BookingSuccess';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Contact from './pages/Contact';

// Admin Pages (lazy loaded)
import { lazy, Suspense } from 'react';
import { PageLoader } from './components/common/LoadingSpinner';

const AdminLogin = lazy(() => import('./pages/admin/Login'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminServices = lazy(() => import('./pages/admin/Services'));
const AdminProjects = lazy(() => import('./pages/admin/Projects'));
const AdminAppointments = lazy(() => import('./pages/admin/Appointments'));
const AdminSettings = lazy(() => import('./pages/admin/Settings'));
const AdminBlog = lazy(() => import('./pages/admin/Blog'));
const AdminNewsletter = lazy(() => import('./pages/admin/Newsletter'));
const AdminContacts = lazy(() => import('./pages/admin/Contacts'));

function PublicLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow">{children}</main>
      <Footer />
    </div>
  );
}

function App() {
  const auth = useAuthProvider();

  return (
    <AuthContext.Provider value={auth}>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
          <Route path="/return-to-india" element={<PublicLayout><ReturnToIndia /></PublicLayout>} />
          <Route path="/real-estate" element={<PublicLayout><RealEstate /></PublicLayout>} />
          <Route path="/real-estate/:slug" element={<PublicLayout><ProjectDetail /></PublicLayout>} />
          <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
          <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
          <Route path="/blog" element={<PublicLayout><Blog /></PublicLayout>} />
          <Route path="/blog/:slug" element={<PublicLayout><BlogPost /></PublicLayout>} />
          <Route path="/book" element={<PublicLayout><BookConsultation /></PublicLayout>} />
          <Route path="/booking/success" element={<PublicLayout><BookingSuccess /></PublicLayout>} />

          {/* Admin Routes */}
          <Route
            path="/admin/login"
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminLogin />
              </Suspense>
            }
          />
          <Route
            path="/admin"
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminDashboard />
              </Suspense>
            }
          />
          <Route
            path="/admin/services"
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminServices />
              </Suspense>
            }
          />
          <Route
            path="/admin/projects"
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminProjects />
              </Suspense>
            }
          />
          <Route
            path="/admin/appointments"
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminAppointments />
              </Suspense>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminSettings />
              </Suspense>
            }
          />
          <Route
            path="/admin/blog"
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminBlog />
              </Suspense>
            }
          />
          <Route
            path="/admin/newsletter"
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminNewsletter />
              </Suspense>
            }
          />
          <Route
            path="/admin/contacts"
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminContacts />
              </Suspense>
            }
          />

          {/* 404 */}
          <Route
            path="*"
            element={
              <PublicLayout>
                <div className="min-h-[60vh] flex items-center justify-center">
                  <div className="text-center">
                    <h1 className="text-4xl font-bold text-gray-900">404</h1>
                    <p className="mt-2 text-gray-600">Page not found</p>
                    <a href="/" className="mt-4 inline-block text-primary-600 hover:underline">
                      Go home
                    </a>
                  </div>
                </div>
              </PublicLayout>
            }
          />
        </Routes>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
